import { createFileRoute } from "@tanstack/react-router";
import { Hydrate } from "@tanstack/react-start";
import { idle, visible } from "@tanstack/react-start/hydration";

import {
  Phone,
  MapPin,
  Mail,
  Star,
  ArrowRight,
  Hammer,
  Home,
  PaintRoller,
  DoorOpen,
  Layers,
  Wrench,
  Facebook,
  Clock,
  ShieldCheck,
  Users,
  BrickWall,
} from "lucide-react";

import { Reveal } from "@/components/Reveal";
import heroImg720 from "@/assets/hero-deck-720.webp";
import { EstimateForm } from "@/components/EstimateForm";
import {
  SITE_URL,
  PHONE,
  PHONE_HREF,
  EMAIL,
  ADDRESS,
  HOURS,
  SERVICE_AREA,
  GOOGLE_PROFILE,
  FACEBOOK,
} from "@/lib/site";
import heroImg960 from "@/assets/hero-deck-960.webp";
import heroImg1440 from "@/assets/hero-deck-1440.webp";
import heroImg1920 from "@/assets/hero-deck-1920.webp";
import logo from "@/assets/logo.webp";
import sidingImg from "@/assets/siding-lift.webp";
import interiorImg from "@/assets/interior-remodel.webp";
import patioImg from "@/assets/patio-cover.webp";
import serviceDeckImg700 from "@/assets/service-deck-700.webp";
import serviceDeckImg1100 from "@/assets/service-deck-1100.webp";
import serviceRemodelImg650 from "@/assets/service-remodel-650.webp";
import serviceRemodelImg1000 from "@/assets/service-remodel-1000.webp";
import serviceKitchenImg650 from "@/assets/service-kitchen-650.webp";
import serviceKitchenImg1000 from "@/assets/service-kitchen-1000.webp";

const heroSrcSet = `${heroImg720} 720w, ${heroImg960} 960w, ${heroImg1440} 1440w, ${heroImg1920} 1920w`;
/** Half-column inside max-w-6xl (1152px) with px-5 gutters */
const serviceImageSizes =
  "(min-width: 1152px) 576px, (min-width: 768px) calc(50vw - 1.25rem), calc(100vw - 2.5rem)";

const TITLE = "Berger Contracting & Home Remodeling | Grand Forks, ND";
const DESCRIPTION =
  "Family-owned Grand Forks contractor since 2006. Full home remodeling, kitchens, baths, additions, custom decks, drywall, siding and handyman services. Free estimates.";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "GeneralContractor",
  name: "Berger Contracting and Home Remodeling",
  url: SITE_URL,
  description: DESCRIPTION,
  telephone: "+1-701-314-0177",
  email: EMAIL,
  foundingDate: "2006",
  address: {
    "@type": "PostalAddress",
    streetAddress: "830 S 48th St Shop 10",
    addressLocality: "Grand Forks",
    addressRegion: "ND",
    postalCode: "58201",
    addressCountry: "US",
  },
  areaServed: "Grand Forks, East Grand Forks and surrounding communities",
  openingHours: "Mo-Fr 08:00-17:00",
  sameAs: [FACEBOOK, GOOGLE_PROFILE],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5",
    reviewCount: "3",
  },
  review: [
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Kenneth Larson" },
      reviewRating: { "@type": "Rating", ratingValue: "5" },
      reviewBody:
        "We couldn't be happier with the work they have done on our patio and the other projects around our home and property.",
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Travis Carpenter" },
      reviewRating: { "@type": "Rating", ratingValue: "5" },
      reviewBody: "Jason and his crew gutted out our 800 Sq Ft basement... We were very pleased.",
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Christine Olimb" },
      reviewRating: { "@type": "Rating", ratingValue: "5" },
      reviewBody: "Reshingled our home beautiful",
    },
  ],
};

export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE_URL}/` },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [
      { rel: "canonical", href: `${SITE_URL}/` },
      {
        rel: "preload",
        as: "image",
        href: heroImg960,
        type: "image/webp",
        imageSrcSet: heroSrcSet,
        imageSizes: "100vw",
      },

    ],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(jsonLd) }],
  }),
});

const primaryServices = [
  {
    icon: Hammer,
    title: "Custom Decks & Repairs",
    cta: "Request a deck estimate",
    copy: "New builds and structural repairs designed for North Dakota weather. From ground-level platforms to multi-level entertainment decks, we frame, finish and maintain outdoor living spaces that last through every season — and look good doing it.",
    image: serviceDeckImg700,
    srcSet: `${serviceDeckImg700} 700w, ${serviceDeckImg1100} 1100w`,
    sizes: serviceImageSizes,
    imageAlt: "Finished multi-level deck and outdoor living space on a modern home",
    width: 700,
    height: 438,
  },
  {
    icon: Home,
    title: "Full Home Remodeling",
    cta: "Get a remodeling quote",
    copy: "Whole-house transformations handled start to finish by one trusted crew. We coordinate framing, finishes and trade partners so you get a single point of contact — and a home that feels put-together, not pieced-together.",
    image: serviceRemodelImg650,
    srcSet: `${serviceRemodelImg650} 650w, ${serviceRemodelImg1000} 1000w`,
    sizes: serviceImageSizes,
    imageAlt: "Bright remodeled living room with hardwood floors and modern furnishings",
    width: 650,
    height: 406,
  },
  {
    icon: Layers,
    title: "Kitchens & Bathrooms",
    cta: "Plan your kitchen or bath",
    copy: "Dated rooms rebuilt into spaces you actually want to spend time in. Layout, cabinets, tile, fixtures and finishes — planned carefully and finished cleanly, without the chaos of juggling separate contractors.",
    image: serviceKitchenImg650,
    srcSet: `${serviceKitchenImg650} 650w, ${serviceKitchenImg1000} 1000w`,
    sizes: serviceImageSizes,
    imageAlt: "Modern renovated kitchen with white cabinetry, marble counters and tile backsplash",
    width: 650,
    height: 406,
  },
] as const;

const secondaryServices = [
  { icon: Wrench, title: "Home Additions" },
  { icon: PaintRoller, title: "Drywall & Interior Paint" },
  { icon: DoorOpen, title: "Doors, Windows & Siding" },
  { icon: BrickWall, title: "Interior Demolition" },
  { icon: Hammer, title: "General Handyman Services" },
];

const featuredService = primaryServices[0];
const FeaturedIcon = featuredService.icon;
const otherPrimaryServices = primaryServices.slice(1);

const reviews = [
  {
    name: "Kenneth Larson",
    text: "We couldn't be happier with the work they have done on our patio and the other projects around our home and property. Jason was patient as our plans evolved, and always took the time to explain our options and answer questions. It was clear throughout the project that he genuinely cared about getting the details right and making sure we were happy with the final result.",
  },
  {
    name: "Travis Carpenter",
    text: "Jason and his crew gutted out our 800 Sq Ft basement which included the 1980's bathroom and several walls and ceiling, leveled and floated the existing concrete, and patched around the windows before waterproofing the entire basement. We were very pleased.",
  },
  { name: "Christine Olimb", text: "Reshingled our home beautiful" },
];

const steps = [
  {
    n: "01",
    t: "Tell us about the project",
    d: "Send the form or call. Share what you have in mind — big or small.",
  },
  {
    n: "02",
    t: "We reach out in 24 hours",
    d: "We talk through details, answer questions and schedule an on-site visit.",
  },
  {
    n: "03",
    t: "Clear, accurate estimate",
    d: "Honest pricing and a scope you understand before anything starts.",
  },
  {
    n: "04",
    t: "Craftsmanship, start to finish",
    d: "Reliable work and clear communication through every stage.",
  },
];

function Stars({ className = "" }: { className?: string }) {
  return (
    <div
      className={`flex gap-0.5 ${className}`}
      role="img"
      aria-label="5 out of 5 stars"
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} className="h-4 w-4 fill-accent text-accent" aria-hidden="true" />
      ))}
    </div>
  );
}

function LicensedBadge({ tone = "light" }: { tone?: "light" | "dark" }) {
  const isDark = tone === "dark";
  return (
    <p
      className={`inline-flex items-center gap-2 text-sm font-semibold tracking-[0.06em] uppercase ${
        isDark ? "text-primary-foreground/85" : "text-foreground"
      }`}
    >
      <ShieldCheck
        className={`h-5 w-5 shrink-0 ${isDark ? "text-accent" : "text-accent"}`}
        aria-hidden="true"
      />
      Licensed & insured
    </p>
  );
}

/** Isolated so IntersectionObserver state does not re-render the whole page. */
function MobileStickyCta() {
  return (
    <div
      className="mobile-sticky-cta fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 backdrop-blur-md contain-layout md:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="grid grid-cols-2 gap-2 px-3 py-2">
        <a
          href={PHONE_HREF}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-sm border border-border bg-card px-3 text-sm font-semibold text-foreground"
        >
          <Phone className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
          Call {PHONE}
        </a>
        <a
          href="#estimate"
          className="inline-flex min-h-11 items-center justify-center rounded-sm bg-accent px-3 text-sm font-semibold tracking-[0.08em] text-accent-foreground uppercase"
        >
          Free Estimate
        </a>
      </div>
    </div>
  );
}

function Index() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <a
        href="#main-content"
        className="sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:m-0 focus:inline-flex focus:h-auto focus:w-auto focus:overflow-visible focus:rounded-sm focus:bg-primary focus:px-5 focus:py-3 focus:text-sm focus:font-semibold focus:tracking-[0.08em] focus:whitespace-normal focus:text-primary-foreground focus:uppercase focus:shadow-[var(--shadow-soft)] focus:[clip-path:none]"
      >
        Skip to content
      </a>

      {/* Header */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-border/60 bg-background/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3">
          <a href="#main-content" className="flex min-h-11 items-center gap-3 rounded-sm">
            <img
              src={logo}
              alt="Berger Contracting and Home Remodeling logo"
              width={48}
              height={48}
              className="h-11 w-11 object-contain"
              decoding="async"
            />
            <span className="hidden font-display text-sm leading-tight font-semibold tracking-tight sm:block">
              Berger Contracting
              <span className="block text-[0.65rem] tracking-[0.2em] text-muted-foreground uppercase">
                & Home Remodeling
              </span>
            </span>
          </a>
          <nav
            className="hidden items-center gap-8 text-sm font-medium md:flex"
            aria-label="Primary"
          >
            <a
              href="#services"
              className="inline-flex min-h-11 items-center transition-colors hover:text-accent"
            >
              Services
            </a>
            <a
              href="#work"
              className="inline-flex min-h-11 items-center transition-colors hover:text-accent"
            >
              Our Work
            </a>
            <a
              href="#reviews"
              className="inline-flex min-h-11 items-center transition-colors hover:text-accent"
            >
              Reviews
            </a>
            <a
              href="#about"
              className="inline-flex min-h-11 items-center transition-colors hover:text-accent"
            >
              About
            </a>
          </nav>
          <div className="flex items-center gap-3">
            <a
              href={PHONE_HREF}
              className="hidden min-h-11 items-center gap-2 text-sm font-semibold lg:flex"
            >
              <Phone className="h-4 w-4 text-accent" aria-hidden="true" />
              {PHONE}
            </a>
            <a
              href="#estimate"
              className="inline-flex min-h-11 items-center rounded-sm bg-primary px-4 text-xs font-semibold tracking-[0.12em] text-primary-foreground uppercase transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              Free Estimate
            </a>
          </div>
        </div>
      </header>

      <main id="main-content">
        {/* Hero */}
        <section
          id="hero"
          className="relative flex min-h-[92vh] items-center overflow-hidden pt-24"
        >
          <img
            src={heroImg960}
            srcSet={heroSrcSet}
            sizes="100vw"
            alt="Newly built cedar deck on a remodeled home at sunset"
            width={960}
            height={544}
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-[oklch(0.21_0.05_264/0.78)]" />
          <div className="relative mx-auto w-full max-w-6xl px-5 py-24">
            {/*
              Hero copy must paint immediately for LCP. Do not wrap in <Reveal>
              (opacity: 0 until JS) — use a transform-only intro instead.
            */}
            <div className="hero-intro max-w-2xl">
              <p className="eyebrow">Grand Forks, North Dakota · Family owned since 2006</p>
              <h1 className="mt-5 font-display text-4xl leading-[1.05] font-semibold text-primary-foreground sm:text-6xl">
                Remodeling done right, by the same crew from start to finish.
              </h1>
              <p className="mt-6 max-w-xl text-lg text-primary-foreground/90">
                Full-home remodels, additions, custom decks and everything in between — reliable
                craftsmanship, honest pricing and clear communication. No hassle, no runaround.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <a
                  href="#estimate"
                  className="group inline-flex min-h-11 items-center gap-2 rounded-sm bg-accent px-7 py-4 text-sm font-semibold tracking-[0.12em] text-accent-foreground uppercase shadow-[var(--shadow-lift)] transition-transform hover:-translate-y-0.5"
                >
                  Get a free estimate
                  <ArrowRight
                    className="h-4 w-4 transition-transform group-hover:translate-x-1"
                    aria-hidden="true"
                  />
                </a>
                <a
                  href={PHONE_HREF}
                  className="inline-flex min-h-11 items-center gap-2 border border-primary-foreground/45 px-7 py-4 text-sm font-semibold tracking-[0.12em] text-primary-foreground uppercase transition-colors hover:border-accent hover:text-accent"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  {PHONE}
                </a>
              </div>
              <div className="mt-10 flex items-center gap-3">
                <Stars />
                <span className="text-sm text-primary-foreground/90">
                  5.0 rated by Grand Forks homeowners on Google
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Trust bar */}
        <section className="border-b border-border bg-secondary">
          <div className="mx-auto grid max-w-6xl gap-8 px-5 py-10 sm:grid-cols-3">
            {[
              {
                icon: Users,
                t: "Family owned since 2006",
                d: "Same name, same standards, 20 years.",
              },
              {
                icon: ShieldCheck,
                t: "One trusted contractor",
                d: "Small repairs to full remodels.",
              },
              { icon: Clock, t: "24-hour response", d: "We call back and show up on time." },
            ].map((item, i) => (
              <Reveal key={item.t} delay={i * 90} className="flex items-start gap-4">
                <item.icon className="mt-0.5 h-6 w-6 shrink-0 text-accent" aria-hidden="true" />
                <div>
                  <p className="font-display text-base font-semibold">{item.t}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{item.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Services */}
        <section id="services" className="mx-auto max-w-6xl scroll-mt-24 px-5 py-24 sm:py-32">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">What we do</p>
            <h2 className="mt-4 text-3xl sm:text-5xl">
              Every trade you need for the inside and outside of your home.
            </h2>
            <p className="mt-5 text-muted-foreground">
              From a single door swap to gutting a basement, we handle it with the same care — and
              we coordinate cleanly around plumbing and electrical steps.
            </p>
          </Reveal>

          {/* Featured primary: Custom Decks */}
          <Reveal className="mt-14 overflow-hidden rounded-sm border border-border bg-card shadow-[var(--shadow-soft)]">
            <article className="grid md:grid-cols-2">
              <div className="relative min-h-[280px] overflow-hidden md:min-h-[380px]">
                <img
                  src={featuredService.image}
                  srcSet={featuredService.srcSet}
                  sizes={featuredService.sizes}
                  alt={featuredService.imageAlt}
                  width={featuredService.width}
                  height={featuredService.height}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
              <div className="flex flex-col justify-center p-8 sm:p-10 lg:p-12">
                <FeaturedIcon className="h-8 w-8 text-accent" aria-hidden="true" />
                <h3 className="mt-5 text-2xl sm:text-3xl">{featuredService.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {featuredService.copy}
                </p>
                <a
                  href="#estimate"
                  className="group mt-8 inline-flex min-h-11 items-center gap-2 text-sm font-semibold tracking-[0.08em] text-primary uppercase transition-colors hover:text-accent"
                >
                  {featuredService.cta}
                  <ArrowRight
                    className="h-4 w-4 transition-transform group-hover:translate-x-1"
                    aria-hidden="true"
                  />
                </a>
              </div>
            </article>
          </Reveal>

          {/* Remaining primary services */}
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            {otherPrimaryServices.map((s, i) => (
              <Reveal
                key={s.title}
                delay={(i + 1) * 100}
                as="article"
                className="group overflow-hidden rounded-sm border border-border bg-card shadow-[var(--shadow-soft)]"
              >
                <div className="overflow-hidden">
                  <img
                    src={s.image}
                    srcSet={s.srcSet}
                    sizes={s.sizes}
                    alt={s.imageAlt}
                    width={s.width}
                    height={s.height}
                    loading="lazy"
                    decoding="async"
                    className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-7 sm:p-8">
                  <s.icon className="h-6 w-6 text-accent" aria-hidden="true" />
                  <h3 className="mt-4 text-xl sm:text-2xl">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.copy}</p>
                  <a
                    href="#estimate"
                    className="group/link mt-6 inline-flex min-h-11 items-center gap-2 text-sm font-semibold tracking-[0.08em] text-primary uppercase transition-colors hover:text-accent"
                  >
                    {s.cta}
                    <ArrowRight
                      className="h-4 w-4 transition-transform group-hover/link:translate-x-1"
                      aria-hidden="true"
                    />
                  </a>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Secondary services */}
          <Reveal className="mt-12">
            <p className="text-sm font-semibold tracking-[0.14em] text-muted-foreground uppercase">
              Also available
            </p>
            <ul className="mt-5 grid gap-px overflow-hidden rounded-sm bg-border sm:grid-cols-2 lg:grid-cols-5">
              {secondaryServices.map((s) => (
                <li
                  key={s.title}
                  className="flex items-center gap-3 bg-card px-5 py-4 transition-colors hover:bg-secondary"
                >
                  <s.icon className="h-5 w-5 shrink-0 text-accent" aria-hidden="true" />
                  <span className="text-sm font-medium leading-snug">{s.title}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </section>

        {/* Work */}
        <section id="work" className="scroll-mt-24 surface-navy">
          <div className="mx-auto max-w-6xl px-5 py-24 sm:py-32">
            <Reveal className="max-w-2xl">
              <p className="eyebrow">Recent projects</p>
              <h2 className="mt-4 text-3xl text-primary-foreground sm:text-5xl">
                Real homes around Grand Forks.
              </h2>
            </Reveal>
            <div className="mt-14 grid gap-6 md:grid-cols-3">
              {[
                {
                  src: sidingImg,
                  alt: "Crew installing new siding on a home from a lift",
                  t: "Exterior siding install",
                },
                {
                  src: interiorImg,
                  alt: "Knotty pine interior remodel with new hardwood flooring",
                  t: "Interior build-out",
                },
                {
                  src: patioImg,
                  alt: "Covered patio addition with timber posts behind a home",
                  t: "Covered patio addition",
                },
              ].map((p, i) => (
                <Reveal
                  key={p.t}
                  delay={i * 120}
                  className="group overflow-hidden rounded-sm bg-primary-foreground/5"
                >
                  <div className="overflow-hidden">
                    <img
                      src={p.src}
                      alt={p.alt}
                      width={382}
                      height={510}
                      loading="lazy"
                      decoding="async"
                      className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <p className="px-5 py-4 text-sm font-semibold tracking-[0.12em] text-primary-foreground/85 uppercase">
                    {p.t}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section id="reviews" className="mx-auto max-w-6xl scroll-mt-24 px-5 py-24 sm:py-32">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <p className="eyebrow">Google reviews</p>
              <h2 className="mt-4 text-3xl sm:text-5xl">Homeowners who'd hire us again.</h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-display text-4xl font-semibold">5.0</span>
              <Stars />
            </div>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {reviews.map((r, i) => (
              <Reveal
                key={r.name}
                delay={i * 110}
                as="article"
                className="flex flex-col rounded-sm border border-border bg-card p-8 shadow-[var(--shadow-soft)]"
              >
                <Stars />
                <blockquote className="mt-5 flex-1 text-sm leading-relaxed text-muted-foreground">
                  "{r.text}"
                </blockquote>
                <p className="mt-6 font-display text-base font-semibold">{r.name}</p>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-12 text-center">
            <a
              href={GOOGLE_PROFILE}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex min-h-11 items-center gap-2 rounded-sm bg-primary px-8 py-4 text-sm font-semibold tracking-[0.12em] text-primary-foreground uppercase transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              Read more reviews
              <ArrowRight
                className="h-4 w-4 transition-transform group-hover:translate-x-1"
                aria-hidden="true"
              />
            </a>
          </Reveal>
        </section>

        {/* About */}
        <section id="about" className="scroll-mt-24 border-y border-border bg-secondary">
          <div className="mx-auto grid max-w-6xl items-center gap-14 px-5 py-24 sm:py-32 lg:grid-cols-2">
            <Reveal>
              <p className="eyebrow">Who you're hiring</p>
              <h2 className="mt-4 text-3xl sm:text-5xl">
                Jason Berger, and a crew that treats your house like their own.
              </h2>
              <p className="mt-6 text-muted-foreground">
                Berger Contracting and Home Remodeling has been a family-owned business in Grand
                Forks since 2006. We're not a big, faceless company — you'll talk to the same people
                who do the work, from the first walkthrough to the last coat of paint.
              </p>
              <p className="mt-4 text-muted-foreground">
                Small handyman jobs and major remodels get the same attention: reliable
                craftsmanship, honest pricing and clear communication at every stage.
              </p>
              <dl className="mt-10 grid grid-cols-2 gap-8 sm:grid-cols-3">
                {[
                  ["2006", "Serving Grand Forks"],
                  ["20 yrs", "Hands-on experience"],
                  ["24 hrs", "Response time"],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="font-display text-3xl font-semibold text-accent">{k}</dt>
                    <dd className="mt-1 text-sm text-muted-foreground">{v}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>

            <Reveal delay={140}>
              <ol className="space-y-px overflow-hidden rounded-sm bg-border">
                {steps.map((s) => (
                  <li key={s.n} className="flex gap-5 bg-card p-6">
                    <span className="font-display text-lg font-semibold text-accent">{s.n}</span>
                    <div>
                      <h3 className="text-base">{s.t}</h3>
                      <p className="mt-1 text-sm text-muted-foreground">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </Reveal>
          </div>
        </section>

        {/* Estimate — hydrate when near viewport; form JS stays out of the startup chunk */}
        <section id="estimate" className="mx-auto max-w-6xl scroll-mt-24 px-5 py-24 sm:py-32">
          <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr]">
            <Reveal>
              <p className="eyebrow">Free estimate</p>
              <h2 className="mt-4 text-3xl sm:text-5xl">Tell us what you're planning.</h2>
              <p className="mt-6 text-muted-foreground">
                Send a few details and we'll reach out within 24 hours to talk it through and
                schedule an on-site visit for an accurate estimate.
              </p>
              <ul className="mt-10 space-y-5 text-sm">
                <li className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-accent" aria-hidden="true" />
                  <a
                    href={PHONE_HREF}
                    className="inline-flex min-h-11 items-center font-semibold hover:text-accent"
                  >
                    {PHONE}
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-accent" aria-hidden="true" />
                  <a
                    href={`mailto:${EMAIL}`}
                    className="inline-flex min-h-11 items-center font-semibold hover:text-accent"
                  >
                    {EMAIL}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden="true" />
                  <div>
                    <address className="not-italic text-muted-foreground">{ADDRESS}</address>
                    <p className="mt-1.5 text-muted-foreground">{SERVICE_AREA}</p>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <Clock className="h-5 w-5 shrink-0 text-accent" aria-hidden="true" />
                  <span className="text-muted-foreground">{HOURS}</span>
                </li>
              </ul>
              <div className="mt-8 inline-flex min-h-11 items-center gap-3 rounded-sm border border-border bg-secondary px-4 py-3">
                <LicensedBadge />
              </div>
              <div className="mt-8 overflow-hidden rounded-sm border border-border shadow-[var(--shadow-soft)]">
                <iframe
                  title="Map showing Berger Contracting and Home Remodeling in Grand Forks, ND"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2674.162620620122!2d-97.0999576!3d47.9138908!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x52c686b0e3aaeb4f%3A0x7f8326bb1571dfbc!2sBerger%20Contracting%20and%20Home%20Remodeling!5e0!3m2!1sen!2sus!4v1785937158000!5m2!1sen!2sus"
                  width={600}
                  height={300}
                  style={{ border: 0, width: "100%", height: "300px" }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
            </Reveal>

            <Hydrate when={visible({ rootMargin: "400px" })} prefetch={idle()}>
              <EstimateForm />
            </Hydrate>
          </div>
        </section>
      </main>

      <footer className="surface-navy pb-[calc(4.5rem+env(safe-area-inset-bottom))] md:pb-0">
        <div className="mx-auto grid max-w-6xl gap-10 px-5 py-16 sm:grid-cols-3">
          <div>
            <img
              src={logo}
              alt="Berger Contracting and Home Remodeling logo"
              width={120}
              height={120}
              loading="lazy"
              decoding="async"
              className="h-20 w-20 rounded-sm bg-background object-contain p-1"
            />
            <p className="mt-5 max-w-xs text-sm text-primary-foreground/90">
              Full-home remodeling, additions, decks and handyman services in Grand Forks, ND.
              Family owned since 2006.
            </p>
            <div className="mt-5">
              <LicensedBadge tone="dark" />
            </div>
          </div>
          <div className="text-sm text-primary-foreground/90">
            <p className="eyebrow">Contact</p>
            <p className="mt-4">
              <a href={PHONE_HREF} className="inline-flex min-h-11 items-center hover:text-accent">
                {PHONE}
              </a>
            </p>
            <p className="mt-2">
              <a
                href={`mailto:${EMAIL}`}
                className="inline-flex min-h-11 items-center hover:text-accent"
              >
                {EMAIL}
              </a>
            </p>
            <address className="mt-2 not-italic">{ADDRESS}</address>
            <p className="mt-1.5 text-primary-foreground/80">{SERVICE_AREA}</p>
            <p className="mt-3 flex min-h-11 items-center gap-2 text-primary-foreground/90">
              <Clock className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
              {HOURS}
            </p>
          </div>
          <div className="text-sm text-primary-foreground/90">
            <p className="eyebrow">Follow</p>
            <a
              href={FACEBOOK}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex min-h-11 items-center gap-2 hover:text-accent"
            >
              <Facebook className="h-4 w-4" aria-hidden="true" /> Facebook
            </a>
            <a
              href={GOOGLE_PROFILE}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 flex min-h-11 items-center gap-2 hover:text-accent"
            >
              <Star className="h-4 w-4" aria-hidden="true" /> Google Business Profile
            </a>
          </div>
        </div>
        <div className="border-t border-primary-foreground/15">
          <p className="mx-auto max-w-6xl px-5 py-6 text-xs text-primary-foreground/80">
            © {new Date().getFullYear()} Berger Contracting and Home Remodeling. All rights
            reserved.
          </p>
        </div>
      </footer>

      <MobileStickyCta />
    </div>
  );
}
